import { skipToken } from '@reduxjs/toolkit/query/react'

import { coreApi } from '../../../api/core'
import { useLiveEventsTrackerAccessCheck } from '../../account/hooks/roleHooks'
import { useLiveEventGameAccess } from './useLiveEventGameAccess'

type UseEventGameDataAccessParams = {
  eventId?: string
  trackedGameId?: string
}

/**
 * Whether the Event dialog may request the game's data (tracked events search, per-tab queries) yet.
 *
 * The event getter (`GET /games/tracked/events/{id}`) is the backend's own access check: it answers 400
 * for a game the user has no access to, and every other game-scoped request would be refused the same way.
 * The dialog therefore makes that one request, and the rest waits until it is known to be worth sending:
 *
 * - the plan covers the game (the user has the Live Events Tracker role, the tracked games list and free
 *   games are in, and the game is open), so the requests go out alongside the event getter as they always did;
 * - or the event getter has succeeded, which also covers a game the plan does not list but the backend
 *   opens anyway.
 *
 * A game the plan locks, or whose plan could not be read (no role, or the lists failed), stays held while the event getter is in flight
 * and for good once it fails. A dialog with no event (event type links) has no check to wait for.
 *
 * Reads the event's cache entry without subscribing, so it never adds a request of its own.
 */
export const useEventGameDataAccess = ({ eventId, trackedGameId }: UseEventGameDataAccessParams) => {
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  const { isGameLocked, isLoading, isError } = useLiveEventGameAccess()
  const eventState = coreApi.endpoints.getTrackedEvent.useQueryState(eventId ?? skipToken)

  if (!eventId) return { canRequestGameData: true }

  const planOpensGame = hasLiveEventsTrackerAccess && !isLoading && !isError && !isGameLocked(trackedGameId)
  const eventRefused = eventState.isError
  return { canRequestGameData: eventState.isSuccess || (planOpensGame && !eventRefused) }
}
